/**
 * Page-scoped analytics context (task 0061, horizon issue 16 row G6).
 *
 * The classic JSP host re-emitted the page-scoped dataLayer fields on every
 * document render (breadcrumb category codes into the page category,
 * `PLP_productCategorization`, the search page's `searchKeyword`, plus the
 * structurally-present `PDP_*` keys). Both storefront hosts are an SPA after
 * boot — no document re-render ever re-emits them, so the initial dataLayer
 * literal (see `app/thirdParty`) carries them as empty strings and this module
 * installs the real values per committed route transition instead, in both
 * hosts: the shared router hook (`useRoutePageAnalyticsContext`) derives the
 * values from the route's page-data metadata and calls
 * {@link applyPageAnalyticsContext} here.
 */

import { sendEvent } from "~/app/analytics/ga/sendEvent";
import { setAnalyticsPageContext } from "./pageContextChannel";
import type { ThirdPartyConfig } from "~/app/thirdParty/thirdPartyScripts";

/**
 * Event name for the per-route dataLayer push. The GTM container needs an
 * `event` key to evaluate its variables on a push; no repo-internal
 * convention existed for SPA page views, so the industry-standard
 * `virtualPageView` name was chosen (task 0061). The GTM container's triggers
 * must know this name to consume the context — the dataLayer emission itself
 * is host-side and complete either way.
 */
export const PAGE_VIEW_EVENT = "virtualPageView";

/** Breadcrumb as delivered by the page-data metadata contract; non-category crumbs carry no code. */
interface BreadcrumbWithCategoryCode {
  categoryCode?: string;
}

/** The per-route dataLayer push: exactly the page-scoped subset of classic's per-document emission. */
export interface PageContextDataLayerPush {
  event: typeof PAGE_VIEW_EVENT;
  pageCategory: string;
  searchKeyword: string;
  PLP_productCategorization: string;
  PDP_designation: string;
  PDP_baseType: string;
  PDP_productID: string;
  PDP_preferredProduct: string;
}

/**
 * Joins the breadcrumb category codes with `" > "`, skipping crumbs without a
 * code — the classic JSP loop's semantics (`c:if not empty breadcrumb.categoryCode`).
 * Root-to-leaf order comes from the page-data breadcrumb contract. Empty when
 * the page has no category crumbs (search pages, content pages).
 */
export function breadcrumbCategoryChain(breadcrumbs: readonly BreadcrumbWithCategoryCode[] | undefined): string {
  return (breadcrumbs ?? [])
    .map((crumb) => crumb.categoryCode)
    .filter((code): code is string => typeof code === "string" && code.length > 0)
    .join(" > ");
}

export function buildPageContextPush(input: {
  breadcrumbChain: string;
  searchKeyword?: string;
}): PageContextDataLayerPush {
  return {
    event: PAGE_VIEW_EVENT,
    // Classic read the CMS page's `pageCategory` attribute server-side
    // (`cmsPage.pageCategory`). The page-data contract does not carry that
    // attribute, so the SPA push mirrors classic's fallback (empty) until the
    // metadata contract exposes it.
    pageCategory: "",
    searchKeyword: input.searchKeyword ?? "",
    // Classic `codes`: the breadcrumb category-code chain, populated on PLP/PDP
    // pages, empty elsewhere.
    PLP_productCategorization: input.breadcrumbChain,
    // Classic kept the PDP_* keys in every emission but never populated them —
    // the JSP's product-attribute block sat behind a disabled `c:if` ("The
    // former GTM tag's optional product attribute was never passed"). Replicated
    // as shape parity: present, empty.
    PDP_designation: "",
    PDP_baseType: "",
    PDP_productID: "",
    PDP_preferredProduct: "",
  };
}

/**
 * Installs the page-scoped context for the current route:
 *
 * - the page-context channel's `pageCategory` = the breadcrumb category-code
 *   chain (consumed by `ga.ts` product-click lists and PLP tab activations),
 *   and
 * - the `virtualPageView` dataLayer push carrying the page-scoped fields.
 *
 * Mirrors the classic GTM gate exactly — the former JSP emitted the whole
 * block only inside its `c:if` GTM branch (`googleAnalyticsTrackingId` set,
 * `!isAnalyticsDisabled`, `!isPrerender`), which is the same condition the
 * shared installer uses for its GTM step. `window.dataLayer` presence is
 * deliberately NOT the gate: on today's QA the Kameleoon engine creates a
 * dataLayer without GTM being configured, and classic stays silent there
 * (verified 2026-09-06 — QA classic shells emit empty page-scoped fields).
 */
export function applyPageAnalyticsContext(input: { breadcrumbChain: string; searchKeyword?: string }): void {
  const config = (window as { __SCHA_THIRD_PARTY__?: ThirdPartyConfig }).__SCHA_THIRD_PARTY__;
  const gtm = config?.gtm;

  if (!gtm?.trackingId || gtm.analyticsDisabled === true || config?.prerender === true) {
    return;
  }

  setAnalyticsPageContext({ pageCategory: input.breadcrumbChain });
  sendEvent(buildPageContextPush(input));
}
